import { useMemo } from 'react';
import { useFinanceStore } from '../stores/financeStore.js';
import { useUserStore } from '../stores/userStore.js';
import { useGamificationStore } from '../stores/gamificationStore.js';
import { mkKey, getExpectedThisMonth } from '../lib/calculations.js';
import { calcCurrentStage } from '../lib/steps.js';

/* ══════════════════════════════════════════════════════
   useCurrentStage — single derived selector for the
   5-stage tracker. Consumers (StagesTab, BadgesTab,
   LeaderboardTab, Dashboard) call this hook instead of
   reading `gamificationStore.currentStage` directly,
   which has been removed from the store.

   Returns the same shape StagesTab used to compute
   inline: { stageResults, currentStage, stagePct }.
   ══════════════════════════════════════════════════════ */

export function useCurrentStage() {
  const month = useUserStore(s => s.month);
  const year = useUserStore(s => s.year);
  const streak = useGamificationStore(s => s.streak);
  const txns = useFinanceStore(s => s.txns);
  const budgets = useFinanceStore(s => s.budgets);
  const incomes = useFinanceStore(s => s.incomes);
  const goals = useFinanceStore(s => s.goals);
  const cards = useFinanceStore(s => s.cards);
  const recurring = useFinanceStore(s => s.recurring);
  const nw = useFinanceStore(s => s.nw);

  return useMemo(() => {
    const mk = mkKey(year, month);
    const monthTxns = txns[mk] || [];
    const budget = budgets[mk] || {};
    const actual = {};
    monthTxns.forEach(t => {
      actual[t.category] = (actual[t.category] || 0) + (parseFloat(t.amount) || 0);
    });
    const totalSpent = Object.values(actual).reduce((s, v) => s + v, 0);
    const totalIncome = (incomes || [])
      .reduce((s, src) => s + getExpectedThisMonth(src, year, month), 0);
    const savRate = totalIncome > 0 ? ((totalIncome - totalSpent) / totalIncome) * 100 : 0;
    const txnCount = monthTxns.length;
    const hasBudget = Object.entries(budget)
      .filter(([k]) => !k.startsWith('_'))
      .some(([, v]) => parseFloat(v) > 0);
    const hasRecurring = (recurring || []).length > 0;
    const hasEmergencyGoal = (goals || [])
      .some(g => (g.name || '').toLowerCase().match(/emergency|rainy|safety/));
    const tB = (cards || []).reduce((s, c) => s + (parseFloat(c.balance) || 0), 0);
    const tL = (cards || []).reduce((s, c) => s + (parseFloat(c.limit) || 0), 0);
    const cardUtil = tL > 0 ? (tB / tL * 100) : 0;
    const goalCount = (goals || []).filter(g => g.current < g.target).length;
    const totalAssets = (nw?.assets || []).reduce((s, a) => s + (parseFloat(a.value) || 0), 0);
    const totalDebts = (nw?.debts || []).reduce((s, d) => s + (parseFloat(d.value) || 0), 0);
    const netWorth = totalAssets - totalDebts;
    const goalsCount = (goals || []).length;
    const goalsPct = goalsCount > 0
      ? ((goals || []).filter(g => g.current >= g.target).length / goalsCount) * 100
      : 0;

    return calcCurrentStage({
      income: totalIncome, savRate, streak, txnCount,
      hasBudget, hasRecurring, hasEmergencyGoal,
      cardUtil, goalCount, netWorth, goalsPct,
    });
  }, [txns, budgets, incomes, goals, cards, recurring, nw, year, month, streak]);
}
